import { useQuery } from "@apollo/client";
import { byDesc, sortByComparisons } from "@lyfegen-platform/utils";
import { Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import InfiniteScrollView from "../../../../components/InfiniteScrollView";
import LoadingBox from "../../../../components/LoadingBox";
import SubscriptionForUpdates from "../../../../hooks/subscription-for-updates";
import { TaskStatus, UserTasksConnectionWhere, UserWhere } from "../../../../models/generated/graphql";
import { userPersonalInfoState } from "../../../../store/user";
import tasksStyles from "./inbox-tasks-styles";
import InboxTasksComponent from "./InboxTasksComponent";
import mapInboxTasks from "./map-inbox-tasks";
import { GET_INBOX_TASKS, TASKS_SUBSCRIPTION } from "./queries";
import TasksPlaceholder from "./TasksPlaceholder";
import { GetUserInboxTasksQuery } from "./types/queries.generated";

const initialValue = 100;

interface InboxTasksProps {
	userWhere: UserWhere;
	userTasksWhere: UserTasksConnectionWhere;
}

const Root: React.FC<InboxTasksProps> = ({ userWhere, userTasksWhere }) => {
	const { t } = useTranslation();
	const userData = useRecoilValue(userPersonalInfoState);
	const { inboxScrollableContainer } = tasksStyles();
	const { data, loading, error, refetch } = useQuery<GetUserInboxTasksQuery>(GET_INBOX_TASKS, {
		fetchPolicy: "no-cache",
		variables: {
			where: userWhere,
			taskWhere: userTasksWhere,
			first: initialValue,
		},
		skip: !userWhere || !userTasksWhere,
	});
	const tasks = data && mapInboxTasks(data, userData, data?.flexEntityTypes);
	const maximumTasksOnPage = 12;

	if (error) {
		return (
			<Typography color="error">
				{t("error")}
				{console.error(error)}
			</Typography>
		);
	}

	if (loading) {
		return <LoadingBox fixedHeight={480} />;
	}

	const sortedTasks = (tasks || []).sort(
		sortByComparisons(
			byDesc((e) => e && e.isAssignee && !e.isCompleted && (!e.taskStatus || (e.taskStatus && e.taskStatus === TaskStatus.Unread))),
			byDesc((e) => e && e.taskStatus && e.taskStatus === TaskStatus.Unread),
			byDesc((e) => e && !e.isCompleted && e.taskStatus && e.taskStatus === TaskStatus.Read),
			byDesc((e) => e && e.isCompleted && e.taskStatus && e.taskStatus === TaskStatus.Read)
		)
	);

	function handleSubscriptionUpdate() {
		refetch();
	}

	return (
		<div className={inboxScrollableContainer}>
			<SubscriptionForUpdates onCompleted={handleSubscriptionUpdate} query={TASKS_SUBSCRIPTION} />
			{!loading && !!!tasks?.length && <TasksPlaceholder />}
			{!loading && !!tasks?.length && (
				<InfiniteScrollView
					data={tasks}
					refetch={refetch}
					initialValue={initialValue}
					isInfiniteScrollDisabled={tasks?.length < maximumTasksOnPage}
				>
					<InboxTasksComponent tasks={sortedTasks} />
				</InfiniteScrollView>
			)}
		</div>
	);
};

export default Root;
